// The per-note scorer (issue #3721): reading one microphone frame against a target note, and
// everything that decides what that reading is and whether it counts toward holding the note. Every
// Learn exercise that asks for a sung note shares it; it was extracted from Pitch Match (issue
// #3518), whose session drives it. No React, no DOM, no timers, no audio. Every time in this module
// is absolute ms on the audio clock, carried by the frames (CaptureFrame.timeMs). Its state is a set
// of fields (NoteScore) that a session's note state extends: a session hands its own note in and
// gets it back with only those fields changed. The session keeps the rest for itself - when scoring
// starts and stops (hearFrame for a frame it does not score, scoreFrame for one it does), the guide,
// and the outcomes that `locked` and `tooNoisy` lead to.
//
// Reading a frame. Silence is anything below voicedDbfs - but only until a voice has been heard:
// from then on the bar drops to unvoicedDbfs (the shared isSilent's `heard` form), so a soft low
// note that dips under the entry bar is still the singer rather than the room (issue #3518,
// Michael's low F on a laptop). And a voice that is still clearly there is kept below even that
// (the held voice, issue #3708). On the Galaxy A16 the phone's own processing turned a held note
// down about 30 dB within 100 to 150 ms, from about -24 to -60 dBFS, and left it at -57 to -67 dBFS
// for up to 2 s, until the next louder attack brought it back 20 to 29 dB, while the estimator went
// on reading the singer at clarity 0.95 to 1.00. In four captures every dot-off span on a held
// note, seven of them, was this gate, and the lower bar could not reach them: it holds only while a
// reading is kept, a kept reading lasts matchGapMs, and 150 ms into the quiet the bar went back up
// to voicedDbfs, under a singer at -55 dBFS on the note at clarity 1.00. So once a reading is kept,
// a frame under the bar and no quieter than heldVoiceDbfs is still the singer when the estimator
// reads it at heldVoiceClarity or better and within jumpCents of the kept reading: the voice that
// was there, where it was (heldVoice). Such a frame is scored like any other - it counts toward the
// hold when it is in tolerance - and, like any other, it refreshes the kept reading, so the bar
// stays down for as long as the voice holds rather than for matchGapMs. A quiet frame the estimator
// cannot read that surely, one it reads further from the voice than that, and any quiet frame
// before a reading is kept - a fresh note's opening - are silence as before. Both rules are
// deliberate and neither is free: on a device whose echo canceller leaks, the piano's own tail
// stays "the singer" for the 6 dB between the two bars after it would otherwise have fallen to
// silence, and down to heldVoiceDbfs where it sits within jumpCents of the voice, so the window in
// which the scorer can be reading the guide back rather than the voice is that much wider. Where
// the app's own canceller runs, the presence gate below takes the frames the guide dominates out
// before either rule is asked; elsewhere, and for the leak the gate lets through, the session's
// note timeout (Pitch Match's noteTimeoutMs) stays the backstop that ends a note the leak would
// otherwise hold open forever.
//
// Presence (issue #3708). On Android the detectors read what the app's own echo canceller left of
// the raw microphone, and it leaves part of the guide: 5 to 24 dB of it removed, depending on the
// phone. In Pitch Match the guide plays the target itself, so a frame of that leftover reads to the
// known-target estimator exactly as the singer on the note would: on three of the four double-talk
// fixtures, offline, 39 to 73 percent of the frames where the guide played alone counted toward the
// hold. The canceller gives every frame its account (CaptureFrame.echo): how loud the echo it
// estimated and took out was, and how loud what it left is. With nobody singing, what is left is
// the echo it failed to remove, far under its estimate (about 16 to 27 dB on the Galaxy A16); with
// the singer there, what is left is mostly the singer, much nearer the echo or above it. So a frame
// whose residual lies more than echoMarginDb under its echo estimate is the guide (guideDominates),
// and it is silence before anything else is asked of it: never a reading, so it draws no dot and
// adds nothing to the hold; never the held voice; and never noise, so a guide playing alone never
// brings the too-noisy screen closer. A frame with no echo account (iOS, desktop, a replay without
// the guide channel) is read as before, and so is one the canceller subtracted nothing from
// (`cancelled` false): it has no estimate to weigh the frame against, and the usual reason is that
// the guide was silent over the span its echo would come from - a singer singing alone, who must
// not be gated - or that the canceller has not found the guide's delay yet, which it finds 0.1 to
// 0.2 s into the first guide note it hears and keeps while the microphone stays open. The gate has
// two known costs. A singer on the guide's own note loses frames (on the fixtures 0 to 9 points of
// the frames that counted), because the canceller's least-squares fit takes the part of the voice
// that lines up with the guide's partials for echo. And on the Galaxy A03s it does nothing for the
// guide alone: its echo is not linear where the pitch lives, so the leftover sits only about 10 dB
// under the estimate (12 dB for nine frames in ten), no deeper than a singer on the guide's note
// often does, and no margin parts them (docs/learn/audio-research.md, 2026-10-04). The issue's
// second measure, the estimator's correlation at the guide's period against the singer's, cannot
// help with that either: in Pitch Match the guide plays the target, so the two periods are one.
//
// A frame that is not silence is read by the known-target estimator first (its cents are relative
// to the target's own period); YIN reads the singer when the estimator is not confident, or when
// the two disagree in a way only a false peak explains (the cross-check below); sound that neither
// can name is noise, and tooNoisyAfterMs of it within one note raises `tooNoisy`, the session's
// too-noisy screen. The estimator cannot see an octave above by correlation alone - half the
// period also divides the full period - so YIN, whose known errors run an octave down, is trusted
// when it reads an octave up. A singer an octave below shows as a weak correlation at the target's
// period and a strong one at twice it, which is read as an octave-below even when the main
// correlation is too weak to trust on its own.
//
// The cross-check (issue #3587). The estimator searches only estimatorBandCents either side of the
// target's period, and a singer further off than that does not leave it silent: a low hum's
// harmonics line up at fractions of the singer's period inside the band and hand it a false peak.
// The phone captures show such peaks at clarity up to 0.88 while the singer was 220 to 470 cents
// flat and YIN, mostly at clarity 0.98 and above, said so - the dot swung up across the target and
// the words said "Hold it!" and then "A little lower". A peak that strong cannot be refused by a
// clarity bar without refusing the voice with it, so when both detectors are confident their
// readings are compared, the estimator's unfolded (before any octave its own correlations add).
// Within the band of each other they agree, and the estimator's finer reading is kept. Further
// apart, near one of harmonicRatios, YIN has read an overtone or a subharmonic of the voice the
// estimator is reading - its known errors, which the captures show at the 2nd, 4th and 5th
// harmonics and an octave down - and the estimator is kept, with its octave rules. Anywhere else
// the singer is outside the band, the peak is a false one, and YIN reads the frame. With only one
// detector confident nothing is compared. The one case this cannot mend is both detectors wrong at
// once, 3:1 apart, which capture 2 shows for a fifth of a second; it is kept as a known partial.
//
// YIN's own octaves (issue #3587). A YIN reading is taken as an octave off the target when it lies
// within octaveFoldCents of one, not merely within the tolerance: a singer an octave below
// and a little sharp is still an octave below, and the estimator, whose octave correlations search
// the same band as its main one, already reads such a voice so. In a phone capture the two
// alternated on one voice - the estimator at +111, "an octave low is fine", YIN at -1040 and
// unfolded - and the dot bounced a full octave. Folded, YIN's reading gives the advice the
// estimator gives. The window has a price the captures show once: YIN's 2:3 error lands inside it
// for a voice some way flat, and at the opening of a phrase in capture 1, with no reading before
// it, a window of 300 cents folded -953 to +247 an octave low, as wrong as before and on the other
// side of the target. So the window is as narrow as capture 3 allows, 200 cents, which leaves that
// opening as read, and it is paid for only by a singer 300 to 700 cents flat. The estimator's own
// trust in YIN reading an octave above keeps its narrower window (yinOctaveWindowCents), since
// there YIN is overruling a confident correlation.
//
// Two confidences, not one. An estimator reading from dotClarity up draws the dot and the hint but
// is flagged `weak` below voicedClarity, and a weak reading never counts toward the hold: the
// screen can say roughly where a soft voice is without a shaky reading handing out a lock. It says
// so, too - a weak reading on the note is "A little louder" rather than "Hold it!", and its dot
// stays yellow, because green and "Hold it!" both promise a meter that is filling and this one
// cannot fill. And because a half-read correlation is not a confident voice, a weak frame goes on
// feeding noisyMs exactly as an unreadable one does, so the too-noisy screen still comes up in a
// room the detector can only half make a note of (issue #3518). YIN keeps the voicedClarity bar
// throughout, because its errors are far from the target and would fling the dot about. And a
// scored frame that reads nothing at all keeps the reading before it while that one is younger
// than matchGapMs, so one dropped frame does not blink the dot out.
//
// Continuity with the voice (issue #3587). The rules above read each frame on its own, and a frame
// on its own cannot tell an overtone of the voice from a voice that has moved there: in the phone
// captures YIN read the singer's 2nd, 4th and 5th harmonics, and an octave below them, for 50 to
// 140 ms at a time, and the dot leapt away and back. So each reading is weighed against the track -
// the reading the frame before it accepted - by the design rule of the issue's PRD: the target
// explains a reading first, continuity second, and continuity only ever delays a reading that is
// both away from the target and discontinuous with the voice. The target explains a reading by the
// octave rule below, which runs first, so a voice an octave off the target where octaves count
// (Pitch Match's Easy and Medium) is folded onto it and is never a jump. A reading within jumpCents
// of the track is the voice moving - no voice moves further in the 11 ms between two frames - and
// continuity lets it pass as it is. Only a reading further off is weighed, by its distance;
// discontinuity is measured against the voice alone, so a reading that lands on the target from
// further off waits like any other (the cross-check's false peaks sit near the target, and a voice
// that gets there glides, which is continuous). At a harmonic ratio to the track (harmonicRatios,
// in the cross-check's window) it is the voice read at an overtone or a subharmonic, and it is
// folded by that ratio onto the track, keeping its own small deviation and taking the track's
// octave flag, so the dot and the octave line stay with the voice. The fold has a limit, and the
// limit is what keeps it from holding a singer on the wrong octave: folds on every frame for longer
// than leapPersistMs are a singer who has really moved, and the reading is believed. At no harmonic
// ratio it waits a frame with the track shown in its place, and is followed only once the next
// frame agrees with it within jumpCents, so a detector's single bad frame never shows and a jump
// that lasts is followed a frame, 11 ms, late. A leap once believed, either way, is followed until
// it is continuous with the track again, and nothing folds it on the way. Last, the rate cap is the
// backstop: no accepted reading moves from the track faster than maxCentsPerMs, an octave in 120
// ms, which no glide, vibrato or hunting comes near, so what slips past the rest is a twitch rather
// than a leap. The track lives only as long as the readings are consecutive: a breath, a consonant,
// a dropped frame, a new note or a replay leaves a frame with no reading, and the first reading
// after it is shown where it is, even while the screen keeps the last one through its grace, so a
// melodic leap across a breath is never held back. And continuity is earned: a run of readings has
// no say until it has lasted trackMinMs - on a phone, its first three frames - and until then each
// reading is shown where it is, as if there were no track, and one that jumps further than
// jumpCents from the reading before starts the run again from itself. The opening of a phrase is
// where YIN errs most and where there is nothing before it to catch the error, and a track that
// took a wrong first reading, or a glitch among its first, for the voice would hold the right one
// back as a jump from it; in capture 1 the dot was walked down from the wrong side of the target
// and the words said "Hold it!" to a singer 200 cents flat.
// The start of the run, the pending jump and the start of the folds are the only state this keeps,
// and nothing on the screen reads them; the frame reading records what continuity accepted and
// what the guards did to it, which the capture replay marks.
//
// Smoothing. Every scored reading - confident, weak or folded by the octave rule below, and weighed
// against the voice as above - is smoothed before anything reads it: its cents move from the
// reading before it toward the frame's by an exponential weight on the audio-clock time between the
// two (readingSmoothingMs), so the curve is the same at any frame rate. It lives here rather than
// on the screen because the dot used to be smoothed by a CSS transition, and a transition given a
// new target on every frame never completes in WebKit: on the iPhone the dot sat at the reading
// from the singer's last pause while the words were live (issue #3587). One smoothed value means
// the words, the dot, the tolerance, the hold and the lock can never disagree. With no reading to
// start from, or one older than matchGapMs, the frame is shown where it is; a reading kept through
// a dropout is the base the next one smooths from. Only the cents are smoothed: the octave and weak
// flags are the frame's own, so "(an octave low is fine)" and "A little louder" come and go on the
// frame that reads them.
//
// The octave rule. When the params' octaveCounts is set (Pitch Match's Easy and Medium) an octave
// reading is folded onto the target - its cents come out near zero and it carries the octave flag,
// which the screen shows as "(an octave low is fine)" - so a man under a child's target is not told
// he is wrong. Without it (Pitch Match's Hard) the same reading keeps its unfolded cents, an octave
// away, and is simply out of tolerance.
//
// The hold. In-tolerance frames the estimator is confident about add the time since the previous
// frame (capped, so a backgrounded tab cannot hand out a lock) to the hold; anything else - out of
// tolerance, weak, silent, noisy - keeps the hold as long as the singer has been away for no more
// than matchGapMs, so a breath or a detector flicker is forgiven, and drops it beyond that, so the
// next in-tolerance frame starts from zero. Reaching the params' holdSeconds locks the note at that
// frame's time: scoreFrame reports `locked` on that frame and the session records the outcome.

import { learnListeningTunables, PitchLevelParams } from '../../../../../common/src/learn_rules.ts';
import { centsBetween, midiToFrequency } from '../../../audio/pitch/notes.ts';
import { EchoReading } from '../../../audio/pitch/types.ts';
import { confidentPitch, frameDelta, isSilent, levelForDbfs } from '../audio/frame_reading.ts';
import { CaptureFrame } from '../audio/learn_mic_capture.ts';

// The listening thresholds every Learn exercise shares, tuned in learn_rules.ts by the device
// validation pass (issue #3338, slice 8)
const tunables = learnListeningTunables;

// How close to an octave above the target YIN must read for the estimator's reading to be taken as
// an octave above (the Range Finder uses its match window, the same 100 cents, for this)
const yinOctaveWindowCents = 100;

// The ratios at which a detector reads a harmonic of a voice rather than the voice itself, as the
// detector's reading over the voice's. Two readings whose gap in cents lies within
// harmonicWindowCents of one of these are one voice read twice, not two notes: two confident
// detectors on one frame (the cross-check in the header), or a frame's reading and the voice the
// frame before it read (continuity; issue #3587, the phone captures).
const harmonicRatios = [
  2, // YIN on the 2nd harmonic: capture 1, +1012 and +1053 over a voice near -150
  3, // the 3rd harmonic; in capture 2 also the gap between YIN's 4th harmonic and a false peak (the known partial)
  4, // YIN on the 4th harmonic: capture 2, +2025 over a voice near -375
  5, // YIN on the 5th harmonic: capture 1, +2306 to +2382 over a voice near -450
  3 / 2, // YIN's error on a strong 3rd harmonic, a fifth above the voice
  1 / 2, // YIN's own octave-down error: capture 2, -705 under a voice at +490
  1 / 3, // the subharmonic mirroring 3:1
  2 / 3, // the same error a fifth below: capture 1 opens a phrase with -953 to -879 under a voice near -250 to -180
];

const harmonicCents = harmonicRatios.map((ratio) => 1200 * Math.log2(ratio));

// The one of harmonicRatios, in cents, that a gap between two readings lies within the window of;
// the windows are a semitone either side and the ratios several apart, so there is at most one
function harmonicNear(gapCents: number): number | undefined {
  return harmonicCents.find((cents) => Math.abs(gapCents - cents) <= tunables.harmonicWindowCents);
}

// Whether the gap between two readings of one voice, in cents, is one of harmonicRatios
export function isHarmonicGap(gapCents: number): boolean {
  return harmonicNear(gapCents) !== undefined;
}

// ---- State -----------------------------------------------------------------------------------

// What a note is scored against: how close counts, how long it must be held and whether an octave
// off counts. Pitch Match passes its level's PitchLevelParams; another exercise passes its own.
export type NoteScoringParams = Pick<PitchLevelParams, 'toleranceCents' | 'holdSeconds' | 'octaveCounts'>;

// The singer against the target, after the octave rule has been applied
export interface PitchReading {
  // Smoothed from the readings before it rather than this frame's alone (smoothReading), and
  // inTolerance is asked of these cents, so the words, the dot and the hold read one number
  cents: number;
  octave?: 'below' | 'above';
  // The estimator was sure enough to place the singer on the staff but not sure enough to be
  // counted on: the dot and the hint read it, the hold never does
  weak?: boolean;
  inTolerance: boolean;
}

// What the reading guards did to a frame's reading, for the capture replay to mark (issue #3587):
// folded onto the track from an overtone or a subharmonic of it, held at the track while a jump
// waits for the next frame to agree with it, drawn back by the rate cap, or a fold that outlasted
// leapPersistMs let go as a genuine leap. A reading the guards passed as it was carries none.
export type ReadingGuard = 'folded' | 'held' | 'capped' | 'unfolded';

// The reading the latest frame accepted, after the octave rule and before the smoothing, the path
// that read it and what the guards did to it; undefined when that frame read nothing
export type FrameReading = PitchReading & { source: Exclude<ReadingSource, 'none'>; guard?: ReadingGuard };

// One note's score: the fields a session's note state extends
export interface NoteScore {
  lastFrameMs?: number;
  silentMs: number;
  noisyMs: number; // sound without a confident pitch, within this note
  hold?: { heldMs: number; lastInToleranceMs: number };
  reading?: PitchReading; // the latest scored reading; undefined while silent, noisy or not yet listening
  readingAtMs?: number; // when that reading was actually read, so a kept one can grow stale
  frameReading?: FrameReading; // not for the screen: the capture replay and its fixture tests read it (issue #3587)
  // Continuity's bookkeeping, which nothing on the screen reads (see Continuity with the voice in
  // the header), cleared with the frame reading: since when the frames have read the singer without
  // a break or, before the track has earned its say, a jump; a jump away from the track, where
  // it was last read and since when, while it waits for a frame to agree with it or is being
  // followed; and since when every frame's reading has been an overtone or a subharmonic folded back
  // onto the track
  trackSinceMs?: number;
  pendingJump?: { cents: number; atMs: number };
  foldedSinceMs?: number;
  level: number; // 0..1 for the meter
}

// The frame reading and continuity's bookkeeping, cleared: no track for the next frame to be
// weighed against
const noTrack: Pick<NoteScore, 'frameReading' | 'trackSinceMs' | 'pendingJump' | 'foldedSinceMs'> = {
  frameReading: undefined,
  trackSinceMs: undefined,
  pendingJump: undefined,
  foldedSinceMs: undefined,
};

// The reading and continuity's bookkeeping, cleared: what a note starts from, and what a session
// clears when it stops listening to hear the guide again
export const noReading: Pick<
  NoteScore,
  'reading' | 'readingAtMs' | 'frameReading' | 'trackSinceMs' | 'pendingJump' | 'foldedSinceMs'
> = {
  reading: undefined,
  readingAtMs: undefined,
  ...noTrack,
};

// A note's score at its beginning: nothing accumulated and everything the note before it left
// behind cleared. The meter level and the last frame's time carry over from that note, so the
// meter does not blink and the first frame of the new note accounts for its own elapsed time
// rather than none.
export function freshNoteScore(previous?: NoteScore): NoteScore {
  return {
    lastFrameMs: previous?.lastFrameMs,
    silentMs: 0,
    noisyMs: 0,
    hold: undefined,
    ...noReading,
    level: previous?.level ?? 0,
  };
}

// ---- Frames ----------------------------------------------------------------------------------

// A frame the session is not scoring: the meter level and the frame's time are taken, and nothing
// is accepted, so a frame that is silent, noisy or not scored at all leaves no frame reading
// behind, and so no track for continuity to weigh the next one against, while the kept reading
// stays for the screen. scoreFrame starts from the same.
export function hearFrame<S extends NoteScore>(score: S, frame: CaptureFrame): S {
  return { ...score, level: levelForDbfs(frame.dbfs), lastFrameMs: frame.timeMs, ...noTrack };
}

// A frame the session is scoring, against `targetMidi` with `params`. `locked` is set on the frame
// that completes the hold, and `tooNoisy` on a frame that brings the note's sound with no confident
// pitch in it to tooNoisyAfterMs; what the session does about either is its own. Neither is
// remembered here: a session that went on scoring a note past its lock would be told `locked` again
// on every in-tolerance frame, so it records the outcome and stops scoring, as Pitch Match does.
export function scoreFrame<S extends NoteScore>(
  score: S,
  frame: CaptureFrame,
  targetMidi: number,
  params: NoteScoringParams,
): { score: S; locked: boolean; tooNoisy: boolean } {
  const delta = frameDelta(score.lastFrameMs, frame.timeMs);
  const base = hearFrame(score, frame);
  const kept = holdAfterGap(score.hold, frame.timeMs);
  const keptReading = readingAfterGap(score, frame.timeMs);
  // A frame the guide dominates is silence before anything else is asked of it (Presence, in the
  // header). Otherwise silence is the room rather than a voice; a singer already being read stays
  // read down to the lower bar (isSilent's `heard` form), and below it while the voice is still
  // clearly there (heldVoice): see Reading a frame in the header.
  const silent =
    guideDominates(frame.echo) ||
    (isSilent(frame, score.reading !== undefined) && !heldVoice(frame, keptReading.reading));
  if (silent) {
    const silentMs = score.silentMs + delta;
    return { score: { ...base, ...keptReading, silentMs, noisyMs: 0, hold: kept }, locked: false, tooNoisy: false };
  }
  const raw = rawReading(frame, targetMidi);
  if (!raw) {
    const noisyMs = score.noisyMs + delta;
    const noisy: S = { ...base, ...keptReading, silentMs: 0, noisyMs, hold: kept };
    return { score: noisy, locked: false, tooNoisy: noisyMs >= tunables.tooNoisyAfterMs };
  }
  const accepted = continueVoice(readingFor(raw, params), score, frame.timeMs, params);
  const reading = smoothReading(accepted.frameReading, score, frame.timeMs, params);
  // A weak reading is worth a dot and a hint and nothing more. It counts neither for the hold nor
  // against it, exactly as a frame with no reading in it does - and, like one, it goes on feeding
  // the too-noisy backstop, because a correlation this shaky is not a confident voice: a room the
  // detector can only half make a note of must still be able to raise the too-noisy screen
  // (issue #3518).
  if (reading.weak) {
    const noisyMs = score.noisyMs + delta;
    const weak: S = {
      ...base,
      silentMs: 0,
      noisyMs,
      reading,
      readingAtMs: frame.timeMs,
      ...accepted,
      hold: kept,
    };
    return { score: weak, locked: false, tooNoisy: noisyMs >= tunables.tooNoisyAfterMs };
  }
  const heard: S = {
    ...base,
    silentMs: 0,
    noisyMs: 0,
    reading,
    readingAtMs: frame.timeMs,
    ...accepted,
  };
  if (!reading.inTolerance) {
    return { score: { ...heard, hold: kept }, locked: false, tooNoisy: false };
  }
  const heldMs = kept ? kept.heldMs + delta : 0; // a hold counts only the time between consecutive frames
  const hold = { heldMs, lastInToleranceMs: frame.timeMs };
  return { score: { ...heard, hold }, locked: heldMs >= params.holdSeconds * 1000, tooNoisy: false };
}

// A hold survives a dropout shorter than matchGapMs
function holdAfterGap(hold: NoteScore['hold'], timeMs: number): NoteScore['hold'] {
  if (hold && timeMs - hold.lastInToleranceMs > tunables.matchGapMs) {
    return undefined;
  }
  return hold;
}

// What the screen keeps showing when a scored frame reads nothing: the reading before it, while
// that one is no older than a hold's own grace, and nothing after that. A detector that drops one
// frame of a soft note should not blink the dot off the staff and back on.
function readingAfterGap(note: NoteScore, timeMs: number): Pick<NoteScore, 'reading' | 'readingAtMs'> {
  const { reading, readingAtMs } = note;
  if (!reading || readingAtMs === undefined || timeMs - readingAtMs > tunables.matchGapMs) {
    return { reading: undefined, readingAtMs: undefined };
  }
  return { reading, readingAtMs };
}

// ---- Reading a frame ---------------------------------------------------------------------------

// Whether a frame under the silence bar is still the voice the kept reading describes, turned down
// by the phone rather than gone (the held voice, in the header): a reading is kept (`kept`, no
// older than matchGapMs), the frame is no quieter than heldVoiceDbfs, and the known-target
// estimator's main reading of it is at heldVoiceClarity or better and within jumpCents of the kept
// reading. The estimator's cents are compared as it reports them, before any octave its own
// correlations add, with the kept reading after the octave rule: a voice folded onto the target
// from an octave off is compared where it is shown, and on a level where octaves do not count one
// shown an octave away is never held. This is the one place a quiet frame is taken for the singer
// rather than the guide's tail or the room; a frame the canceller's account shows to be the guide
// (guideDominates) never reaches it.
function heldVoice(frame: CaptureFrame, kept: PitchReading | undefined): boolean {
  const { heldVoiceDbfs, heldVoiceClarity, jumpCents } = tunables;
  const target = frame.target;
  if (!kept || !target || frame.dbfs < heldVoiceDbfs) {
    return false;
  }
  return target.clarity >= heldVoiceClarity && Math.abs(target.cents - kept.cents) <= jumpCents;
}

// Whether the app's own echo canceller's account of a frame shows the guide rather than the singer
// (Presence, in the header): it subtracted an echo estimate from the window, and what it left lies
// more than echoMarginDb under that estimate. No account, or one where nothing was subtracted, is
// never the guide. `marginDb` is for the test that sweeps the margin; the scorer uses the tunable.
export function guideDominates(echo: EchoReading | undefined, marginDb = tunables.echoMarginDb): boolean {
  return Boolean(echo?.cancelled && echo.residualDbfs < echo.echoDbfs - marginDb);
}

// The singer against the target before the octave rule: cents as the detectors report
// them, an octave away when that is what they read
interface RawReading {
  cents: number;
  octave?: 'below' | 'above';
  // The path that read it; 'weak' is the estimator placing the singer without being confident enough
  // to be counted on. The replay names the path so a flicker can be traced to the detector that
  // produced it (issue #3587).
  source: Exclude<ReadingSource, 'none'>;
}

// Which of rawReading's paths reads a frame: the known-target estimator (its octave-below
// correlation included), YIN, the estimator's weak reading, or none of them
export type ReadingSource = 'estimator' | 'yin' | 'weak' | 'none';

function rawReading(frame: CaptureFrame, targetMidi: number): RawReading | undefined {
  const { voicedClarity, dotClarity, octaveMargin, octaveFoldCents } = tunables;
  const target = frame.target;
  const pitch = confidentPitch(frame);
  const yinCents = pitch ? centsBetween(pitch.frequency, midiToFrequency(targetMidi)) : undefined;
  if (target && target.clarity >= voicedClarity && !onFalsePeak(target.cents, yinCents)) {
    if (target.octaveBelow >= target.clarity + octaveMargin) {
      return { cents: target.cents - 1200, octave: 'below', source: 'estimator' };
    }
    // The estimator cannot tell a singer on the target from one an octave above by correlation
    // alone (half the period also divides the full period), so YIN, whose known errors run an
    // octave down, is trusted when it reads an octave up.
    const yinOctaveAbove = yinCents !== undefined && Math.abs(yinCents - 1200) <= yinOctaveWindowCents;
    if (target.octaveAbove >= target.clarity + octaveMargin || yinOctaveAbove) {
      return { cents: target.cents + 1200, octave: 'above', source: 'estimator' };
    }
    return { cents: target.cents, source: 'estimator' };
  }
  // YIN alone, or overruling a false peak. Within octaveFoldCents of an octave off the target it is
  // that octave, as the estimator would read the same voice (see YIN's own octaves in the header).
  if (yinCents !== undefined) {
    if (Math.abs(yinCents + 1200) <= octaveFoldCents) {
      return { cents: yinCents, octave: 'below', source: 'yin' };
    }
    if (Math.abs(yinCents - 1200) <= octaveFoldCents) {
      return { cents: yinCents, octave: 'above', source: 'yin' };
    }
    return { cents: yinCents, source: 'yin' };
  }
  // A singer an octave below reads weakly at the target's period and strongly at twice it, so the
  // octave correlation can name them even when the main one is too weak to trust.
  if (target && target.octaveBelow >= voicedClarity && target.octaveBelow >= target.clarity + octaveMargin) {
    return { cents: target.cents - 1200, octave: 'below', source: 'estimator' };
  }
  // Last, and only for the dot and the hint: the estimator is sure enough to say roughly where the
  // singer is but not sure enough to count. A soft low note reads like this for seconds at a time,
  // and showing them a dot to steer by is better than showing them nothing at all (issue #3518).
  if (target && target.clarity >= dotClarity) {
    return { cents: target.cents, source: 'weak' };
  }
  return undefined;
}

// A confident estimator whose peak a confident YIN shows to be false: the two read further apart
// than the estimator's band and not at a harmonic ratio, so the singer is outside the band and the
// peak inside it is the hum's harmonics lining up (the cross-check in the header). The estimator's
// cents are compared as it reports them, before any octave its own correlations add.
function onFalsePeak(estimatorCents: number, yinCents: number | undefined): boolean {
  if (yinCents === undefined) {
    return false;
  }
  const gapCents = yinCents - estimatorCents;
  return Math.abs(gapCents) > tunables.estimatorBandCents && !isHarmonicGap(gapCents);
}

// The frame's reading with the octave rule applied (see the header), before continuity
function readingFor(raw: RawReading, params: NoteScoringParams): FrameReading {
  const weak = raw.source === 'weak' ? { weak: true } : {};
  if (raw.octave && params.octaveCounts) {
    const cents = raw.cents + (raw.octave === 'below' ? 1200 : -1200);
    return atCents({ octave: raw.octave, ...weak, source: raw.source }, cents, params);
  }
  return atCents({ ...weak, source: raw.source }, raw.cents, params);
}

// A reading placed at `cents`, with the params' tolerance asked of those cents. The octave rule,
// continuity and the smoothing all place a reading through here, so its inTolerance always
// answers for the cents beside it, whichever of them placed it last.
function atCents<R extends Omit<PitchReading, 'cents' | 'inTolerance'>>(
  reading: R,
  cents: number,
  params: NoteScoringParams,
): R & Pick<PitchReading, 'cents' | 'inTolerance'> {
  return { ...reading, cents, inTolerance: Math.abs(cents) <= params.toleranceCents };
}

// ---- Continuity with the voice -----------------------------------------------------------------

// What continuity makes of one frame: the reading the frame accepts, and the bookkeeping it leaves
// for the next frame, each part of which is cleared unless it is set here
interface Continued {
  frameReading: FrameReading;
  trackSinceMs?: number;
  pendingJump?: NoteScore['pendingJump'];
  foldedSinceMs?: number;
}

// This frame's reading weighed against the track, the reading the frame before it accepted, and
// then held to the rate cap (see Continuity with the voice in the header). With no track - the
// frame before read nothing, or there was none on this note - it is shown where it is, and a track
// starts with it. A track younger than trackMinMs has not earned a say yet, so its reading is shown
// where it is too: the opening frames of a phrase are where YIN errs most, and a wrong one taken
// for the voice would hold the right one back. What earns it is trackMinMs of readings that agree,
// so a reading further than jumpCents from the one before starts the track again from itself:
// otherwise a glitch 20 ms into a phrase would be shown as read and then, 10 ms later, be the
// reference the voice coming back is weighed against.
function continueVoice(candidate: FrameReading, note: NoteScore, timeMs: number, params: NoteScoringParams): Continued {
  const { frameReading: track, lastFrameMs, trackSinceMs } = note;
  if (!track || lastFrameMs === undefined || trackSinceMs === undefined) {
    return { frameReading: candidate, trackSinceMs: timeMs };
  }
  if (timeMs - trackSinceMs < tunables.trackMinMs) {
    const jumped = Math.abs(candidate.cents - track.cents) > tunables.jumpCents;
    return { frameReading: candidate, trackSinceMs: jumped ? timeMs : trackSinceMs };
  }
  const continued = weighAgainstTrack(candidate, track, note, lastFrameMs, timeMs, params);
  const frameReading = capRate(continued.frameReading, track, timeMs - lastFrameMs, params);
  return { ...continued, frameReading, trackSinceMs };
}

// A reading within jumpCents of the track is the voice moving. Further off it is an overtone or a
// subharmonic folded back onto the track until the folds outlast leapPersistMs, a jump that waits
// a frame for the next to agree with it, or a jump already agreed with, followed until it is
// continuous with the track again. A jump that has only waited its frame is asked whether it is an
// overtone first; one already being followed is not asked again, because the rate cap walks it
// across the ratios on its way to the voice and folding it at one of them would hold a singer who
// has really leapt a fifth short of where they are.
function weighAgainstTrack(
  candidate: FrameReading,
  track: FrameReading,
  note: NoteScore,
  trackAtMs: number,
  timeMs: number,
  params: NoteScoringParams,
): Continued {
  const gapCents = candidate.cents - track.cents;
  if (Math.abs(gapCents) <= tunables.jumpCents) {
    return { frameReading: candidate };
  }
  const { pendingJump } = note;
  const agrees = pendingJump !== undefined && Math.abs(candidate.cents - pendingJump.cents) <= tunables.jumpCents;
  // first read before the track's own frame, so some frame has already followed it
  const followed = pendingJump !== undefined && pendingJump.atMs < trackAtMs;
  const harmonicCents = harmonicNear(gapCents);
  if (agrees && (followed || harmonicCents === undefined)) {
    return { frameReading: candidate, pendingJump: { cents: candidate.cents, atMs: pendingJump.atMs } };
  }
  if (harmonicCents !== undefined) {
    const foldedSinceMs = note.foldedSinceMs ?? timeMs;
    if (timeMs - foldedSinceMs > tunables.leapPersistMs) {
      // folds on every frame for this long are a singer who has really moved there: believe it,
      // and follow the leap from where it began
      const unfolded: FrameReading = { ...candidate, guard: 'unfolded' };
      return { frameReading: unfolded, pendingJump: { cents: candidate.cents, atMs: foldedSinceMs } };
    }
    // The fold says this is the voice the track already describes, so the track's octave flag, or
    // its lack of one, is the truth about it: an octave read off an overtone is not the singer's
    const { octave: _octave, ...voice } = candidate;
    const folded: FrameReading = { ...voice, ...(track.octave ? { octave: track.octave } : {}), guard: 'folded' };
    return { frameReading: atCents(folded, candidate.cents - harmonicCents, params), foldedSinceMs };
  }
  // The track stands in for this frame, flags, source and all, while the jump waits for the next
  return { frameReading: { ...track, guard: 'held' }, pendingJump: { cents: candidate.cents, atMs: timeMs } };
}

// The rate cap: an accepted reading moves from the track by at most maxCentsPerMs for every ms
// since the track's frame, and is drawn back toward the track when it would move further. A reading
// continuity has already marked keeps its mark: a fold let go as a leap is always drawn back on its
// first frame, and the release is the thing worth seeing there; the frames after it show the cap.
function capRate(
  reading: FrameReading,
  track: FrameReading,
  elapsedMs: number,
  params: NoteScoringParams,
): FrameReading {
  const maxCents = tunables.maxCentsPerMs * elapsedMs;
  const gapCents = reading.cents - track.cents;
  if (Math.abs(gapCents) <= maxCents) {
    return reading;
  }
  const capped: FrameReading = { ...reading, guard: reading.guard ?? 'capped' };
  return atCents(capped, track.cents + Math.sign(gapCents) * maxCents, params);
}

// ---- Smoothing ---------------------------------------------------------------------------------

// The reading before this one, moved part of the way toward the frame's (see Smoothing in the
// header). The part grows with the time since that reading was actually read - not since the last
// frame - so a reading kept through a dropout is smoothed from over the whole gap rather than held
// back by it. With nothing to start from, or a reading older than a hold's own grace, the frame is
// shown where it is: sliding in from where the singer was a breath ago is the bug this replaced.
// The screen's reading does not say which path read the frame, or what the guards did to it; the
// frame reading does.
function smoothReading(latest: FrameReading, note: NoteScore, timeMs: number, params: NoteScoringParams): PitchReading {
  const { source: _source, guard: _guard, ...unsmoothed } = latest;
  const { reading: previous, readingAtMs } = note;
  if (!previous || readingAtMs === undefined || timeMs - readingAtMs > tunables.matchGapMs) {
    return unsmoothed;
  }
  const weight = 1 - Math.exp(-(timeMs - readingAtMs) / tunables.readingSmoothingMs);
  return atCents(unsmoothed, previous.cents + (unsmoothed.cents - previous.cents) * weight, params);
}

// ---- Selectors -------------------------------------------------------------------------------

// What a reading asks of the singer: where they are against the target, weak or not, and on it
// whether the reading can count
export type ReadingHint = 'higher' | 'lower' | 'louder' | 'hold';

export const pitchHigherText = 'A little higher';
export const pitchLowerText = 'A little lower';
// A reading the estimator is not confident about, on the note: the singer is in the right place but
// too quiet to be counted on, so the hold cannot fill. Saying "Hold it!" over a meter that can
// never move would be a lie; this asks for the one thing that would make the reading count.
export const pitchLouderText = 'A little louder';
export const pitchHoldText = 'Hold it!'; // the artboard keeps this wording at a full meter, on the lock too

export function readingHint(reading: PitchReading): ReadingHint {
  if (!reading.inTolerance) {
    return reading.cents < 0 ? 'higher' : 'lower'; // weak or not: where they are is where they are
  }
  return reading.weak ? 'louder' : 'hold';
}

export const octaveLowText = '(an octave low is fine)';
export const octaveHighText = '(an octave high is fine)';

export function octaveText(reading: PitchReading | undefined): string | undefined {
  const octave = reading?.octave;
  if (!octave) {
    return undefined;
  }
  return octave === 'below' ? octaveLowText : octaveHighText;
}

// Where to draw the singer on the staff, and in which colour. Green means the frame counts: in
// tolerance and confidently read (or locked). A weak reading is yellow wherever it sits, because a
// green dot over a hold meter that cannot fill would promise something the reading cannot give.
export function readingDot(
  reading: PitchReading | undefined,
  locked: boolean,
): { cents: number; color: 'yellow' | 'green' } | undefined {
  if (!reading) {
    return undefined;
  }
  const counts = locked || (reading.inTolerance && !reading.weak);
  return { cents: reading.cents, color: counts ? 'green' : 'yellow' };
}

// How far the hold has filled toward the params' holdSeconds; full once the note is locked
export function holdProgressFor(
  score: NoteScore,
  params: NoteScoringParams,
  locked: boolean,
): { heldMs: number; holdMs: number } {
  const holdMs = params.holdSeconds * 1000;
  const heldMs = locked ? holdMs : (score.hold?.heldMs ?? 0);
  return { heldMs: Math.min(heldMs, holdMs), holdMs };
}
